// FacilityDetails.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Star,
  ArrowLeft,
  Cpu,
  Users,
  Calendar,
  Clock,
  Phone,
  Mail,
  CheckCircle,
  Sparkles,
  Building2,
  Award,
  Clock as ClockIcon,
  Calendar as CalendarIcon,
  Share2,
  Heart,
  ChevronRight
} from "lucide-react";
import Navbar from "../components/landingPage/Navbar";
import Footer from "../components/landingPage/Footer";
import "./FacilityDetails.css";

const centres = [
  { 
    id: 1,
    city: "Visakhapatnam", 
    rating: 4.8, 
    machines: 42, 
    experts: 18, 
    utilised: 78,
    address: "Plot No. 123, APIIC Industrial Park, Visakhapatnam, AP - 530001",
    phone: "+91 891-234-5678",
    email: "vizag@apicprotohub.in",
    description: "State-of-the-art prototyping facility with advanced 3D printing and CNC machining capabilities. Equipped with the latest technology to support innovation and rapid prototyping.",
    facilities: ["3D Printing", "CNC Machining", "PCB Prototyping", "Laser Cutting"],
    hours: "Mon-Sat: 9:00 AM - 6:00 PM",
    established: "2020",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop&crop=center&auto=format&q=80"
  },
  { 
    id: 2,
    city: "Vijayawada", 
    rating: 4.7, 
    machines: 36, 
    experts: 14, 
    utilised: 71,
    address: "Survey No. 45, Industrial Area, Vijayawada, AP - 520001",
    phone: "+91 866-345-6789",
    email: "vijayawada@apicprotohub.in",
    description: "Innovation hub with focus on robotics, automation, and product design prototyping. A centre of excellence for advanced manufacturing.",
    facilities: ["Robotics", "Automation", "3D Scanning", "Product Design"],
    hours: "Mon-Sat: 9:00 AM - 6:00 PM",
    established: "2021",
    image: "https://images.unsplash.com/photo-1581092335871-5f2b5e17cd7a?w=800&h=600&fit=crop&crop=center&auto=format&q=80"
  },
  { 
    id: 3,
    city: "Tirupati", 
    rating: 4.6, 
    machines: 29, 
    experts: 12, 
    utilised: 65,
    address: "Plot No. 78, IT Park, Tirupati, AP - 517501",
    phone: "+91 877-456-7890",
    email: "tirupati@apicprotohub.in",
    description: "Advanced manufacturing facility specializing in aerospace components and precision engineering. Serving the aerospace and defense sectors.",
    facilities: ["Precision Engineering", "Aerospace", "Quality Testing", "Material Science"],
    hours: "Mon-Sat: 9:00 AM - 6:00 PM",
    established: "2021",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop&crop=center&auto=format&q=80"
  },
  { 
    id: 4,
    city: "Kurnool", 
    rating: 4.5, 
    machines: 24, 
    experts: 9, 
    utilised: 58,
    address: "Plot No. 234, Industrial Hub, Kurnool, AP - 518001",
    phone: "+91 851-567-8901",
    email: "kurnool@apicprotohub.in",
    description: "Emerging innovation centre with focus on sustainable manufacturing and renewable energy prototyping. Driving green innovation.",
    facilities: ["Sustainable Manufacturing", "Renewable Energy", "IoT", "Smart Sensors"],
    hours: "Mon-Sat: 9:00 AM - 6:00 PM",
    established: "2022",
    image: "https://images.unsplash.com/photo-1581092335871-5f2b5e17cd7a?w=800&h=600&fit=crop&crop=center&auto=format&q=80"
  }
];

export default function FacilityDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [centre, setCentre] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const found = centres.find(c => c.id === parseInt(id));
    setCentre(found || null);
    setLoading(false);
  }, [id]);

  if (loading) {
    return (
      <div className="facility-details-loading">
        <Navbar />
        <div className="facility-details-spinner-container">
          <div className="facility-details-spinner"></div>
          <p>Loading centre details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!centre) {
    return (
      <div className="facility-details-notfound">
        <Navbar />
        <div className="facility-details-notfound-content">
          <div className="facility-details-notfound-icon">
            <Building2 size={48} />
          </div>
          <h2>Centre Not Found</h2>
          <p>The centre you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/facilities')} className="facility-details-back-btn">
            <ArrowLeft size={18} />
            Back to Centres
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="facility-details-page">
      <Navbar />
      
      <div className="facility-details-container">
        {/* Back Button */}
        <button className="facility-details-back" onClick={() => navigate('/facilities')}>
          <ArrowLeft size={20} />
          Back to Centres
        </button>

        {/* Hero Section */}
        <div className="facility-details-hero">
          <div className="facility-details-hero-image">
            <img src={centre.image} alt={`APIC ${centre.city}`} />
            <div className="facility-details-hero-overlay"></div>
            <div className="facility-details-hero-content">
              <div className="facility-details-hero-badge">
                <Sparkles size={16} />
                <span>APIC Centre</span>
              </div>
              <h1 className="facility-details-hero-title">APIC {centre.city}</h1>
              <div className="facility-details-hero-info">
                <div className="facility-details-hero-info-item">
                  <MapPin size={18} />
                  <span>{centre.address}</span>
                </div>
                <div className="facility-details-hero-info-item">
                  <Star size={18} className="facility-details-hero-star" />
                  <span>{centre.rating} Rating</span>
                </div>
              </div>
              <div className="facility-details-hero-actions">
                <button className="facility-details-hero-btn primary">
                  <Calendar size={18} />
                  Book a Machine
                </button>
                <button className="facility-details-hero-btn secondary">
                  <Share2 size={18} />
                  Share
                </button>
                <button className="facility-details-hero-btn secondary">
                  <Heart size={18} />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="facility-details-grid">
          {/* Main Content */}
          <div className="facility-details-main">
            <div className="facility-details-section">
              <h2 className="facility-details-section-title">About This Centre</h2>
              <p className="facility-details-section-text">{centre.description}</p>
            </div>

            <div className="facility-details-section">
              <h2 className="facility-details-section-title">Facilities & Services</h2>
              <div className="facility-details-facilities">
                {centre.facilities.map((facility, index) => (
                  <div key={index} className="facility-details-facility">
                    <CheckCircle size={18} />
                    <span>{facility}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="facility-details-section">
              <h2 className="facility-details-section-title">Quick Stats</h2>
              <div className="facility-details-stats">
                <div className="facility-details-stat">
                  <div className="facility-details-stat-icon">
                    <Cpu size={24} />
                  </div>
                  <div>
                    <p className="facility-details-stat-value">{centre.machines}</p>
                    <p className="facility-details-stat-label">Machines</p>
                  </div>
                </div>
                <div className="facility-details-stat">
                  <div className="facility-details-stat-icon">
                    <Users size={24} />
                  </div>
                  <div>
                    <p className="facility-details-stat-value">{centre.experts}</p>
                    <p className="facility-details-stat-label">Experts</p>
                  </div>
                </div>
                <div className="facility-details-stat">
                  <div className="facility-details-stat-icon">
                    <Award size={24} />
                  </div>
                  <div>
                    <p className="facility-details-stat-value">{centre.utilised}%</p>
                    <p className="facility-details-stat-label">Utilization</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="facility-details-sidebar">
            <div className="facility-details-sidebar-card">
              <h3 className="facility-details-sidebar-title">Quick Info</h3>
              
              <div className="facility-details-sidebar-item">
                <div className="facility-details-sidebar-icon">
                  <ClockIcon size={18} />
                </div>
                <div>
                  <p className="facility-details-sidebar-label">Working Hours</p>
                  <p className="facility-details-sidebar-value">{centre.hours}</p>
                </div>
              </div>

              <div className="facility-details-sidebar-item">
                <div className="facility-details-sidebar-icon">
                  <CalendarIcon size={18} />
                </div>
                <div>
                  <p className="facility-details-sidebar-label">Established</p>
                  <p className="facility-details-sidebar-value">{centre.established}</p>
                </div>
              </div>

              <div className="facility-details-sidebar-divider"></div>

              <div className="facility-details-sidebar-item">
                <div className="facility-details-sidebar-icon">
                  <Phone size={18} />
                </div>
                <div>
                  <p className="facility-details-sidebar-label">Contact</p>
                  <p className="facility-details-sidebar-value">{centre.phone}</p>
                </div>
              </div>

              <div className="facility-details-sidebar-item">
                <div className="facility-details-sidebar-icon">
                  <Mail size={18} />
                </div>
                <div>
                  <p className="facility-details-sidebar-label">Email</p>
                  <p className="facility-details-sidebar-value">{centre.email}</p>
                </div>
              </div>

              <div className="facility-details-sidebar-divider"></div>

              <button className="facility-details-sidebar-book-btn">
                <Calendar size={18} />
                Book This Centre
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}